import { Injectable } from '@angular/core';
import { UserPresenceActivityService } from './user-presence-activity.service';

type ChatLinkNotificationOptions = NotificationOptions & {
  renotify?: boolean;
  timestamp?: number;
};

@Injectable({
  providedIn: 'root',
})
export class NotificationSystemService {
  private readonly STORAGE_KEY = 'chatlink_notifications_enabled';
  private readonly COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365 * 5;
  private readonly NOTIFICATION_ICON = '/icons/cisb-pwa-192.png';
  private notificationSequence = 0;

  constructor(private userPresenceActivityService: UserPresenceActivityService) {}

  public async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported()) {
      console.warn('Web Notifications API no soportada en este navegador');
      return 'denied';
    }

    if (Notification.permission !== 'default') {
      return Notification.permission;
    }

    try {
      return await Notification.requestPermission();
    } catch (error) {
      console.error('Error solicitando permiso de notificaciones:', error);
      return 'denied';
    }
  }

  public areNotificationsEnabled(): boolean {
    return this.getCookie(this.STORAGE_KEY) === 'true';
  }

  public setNotificationsEnabled(enabled: boolean): void {
    document.cookie = `${this.STORAGE_KEY}=${String(enabled)}; max-age=${this.COOKIE_MAX_AGE_SECONDS}; path=/; SameSite=Lax`;
  }

  public getPermissionStatus(): NotificationPermission {
    if (!this.isSupported()) {
      return 'denied';
    }

    return Notification.permission;
  }

  public isSupported(): boolean {
    return 'Notification' in window;
  }

  public showNotification(
    title: string,
    options?: ChatLinkNotificationOptions,
  ): void {
    if (this.userPresenceActivityService.isBusy()) {
      return;
    }

    if (!this.isSupported()) {
      console.warn('Web Notifications no soportado');
      return;
    }

    if (!this.areNotificationsEnabled()) {
      console.warn(
        'Notificaciones ChatLink deshabilitadas por preferencia del usuario',
      );
      return;
    }

    if (Notification.permission !== 'granted') {
      console.warn(
        'Permiso de notificaciones no concedido:',
        Notification.permission,
      );
      return;
    }

    try {
      this.createNotification(title, {
        icon: this.NOTIFICATION_ICON,
        badge: this.NOTIFICATION_ICON,
        requireInteraction: false,
        silent: false,
        ...options,
      });
    } catch (error) {
      console.error('Error mostrando notificacion:', error);

      try {
        this.createNotification(title, {
          body: options?.body,
          icon: this.NOTIFICATION_ICON,
          tag: this.createUniqueTag('fallback'),
        });
      } catch (fallbackError) {
        console.error('Error mostrando notificacion basica:', fallbackError);
      }
    }
  }

  private createNotification(
    title: string,
    options: ChatLinkNotificationOptions,
  ): void {
    const notification = new Notification(title, options);
    window.setTimeout(() => notification.close(), 8000);
  }

  private createUniqueTag(type: 'external' | 'internal' | 'internal-mention' | 'fallback'): string {
    this.notificationSequence += 1;
    return `chatlink-${type}-${Date.now()}-${this.notificationSequence}`;
  }

  private getCookie(name: string): string | null {
    const prefix = `${name}=`;
    const cookie = document.cookie
      .split(';')
      .map((item) => item.trim())
      .find((item) => item.startsWith(prefix));

    return cookie ? decodeURIComponent(cookie.substring(prefix.length)) : null;
  }

  private getMessagePreview(messagePreview: string): string {
    return `${messagePreview.substring(0, 90)}${messagePreview.length > 90 ? '...' : ''}`;
  }

  public notifyExternalMessage(
    senderName: string,
    messagePreview: string,
  ): void {
    this.showNotification('Nuevo mensaje de cliente', {
      body: `${senderName}: ${this.getMessagePreview(messagePreview)}`,
      tag: this.createUniqueTag('external'),
      timestamp: Date.now(),
    });
  }

  public notifyInternalMessage(
    senderName: string,
    messagePreview: string,
  ): void {
    this.showNotification('Nuevo mensaje interno', {
      body: `${senderName}: ${this.getMessagePreview(messagePreview)}`,
      tag: this.createUniqueTag('internal'),
      timestamp: Date.now(),
    });
  }

  public notifyInternalMention(senderName: string, messagePreview: string): void {
    this.showNotification('Te mencionaron en un grupo interno', {
      body: `${senderName}: ${this.getMessagePreview(messagePreview)}`,
      tag: this.createUniqueTag('internal-mention'),
      timestamp: Date.now(),
    });
  }
}
